import { Button, FlashMessage, Input, Modal } from "../../common";
import React, { useEffect, useState } from "react";
import SkillsIcon from "../icons/skills-icon.svg";
import { useForm } from "react-hook-form";
import CrossSmall from "../icons/cross-small-white.svg";
import { updateCandidateSkillsRequest } from "../../../reducers/candidate/candidate.reducer";
import { useDispatch, useSelector } from "react-redux";
import { rootReducersState } from "../../../reducers";
import EditIcon from "../icons/edit-icon.svg";
import _ from "lodash";

interface Skill {
  title: string;
  desc: string | null;
}

interface SkillsSectionProps {
  skills: Skill[];
  isPublicPage?: boolean;
  openRequest?: boolean;
  onOpenHandled?: () => void;
}

function SkillsSection({ skills: initialSkills, isPublicPage, openRequest, onOpenHandled }: SkillsSectionProps) {
  const dispatch = useDispatch();

  const [skillsModal, setSkillsModal] = useState(false);
  const [localSkills, setLocalSkills] = useState<Skill[]>(initialSkills || []);
  const [inputValue, setInputValue] = useState("");

  const { handleSubmit, setValue, watch, reset } = useForm<{
    skills: Skill[];
  }>({
    defaultValues: {
      skills: initialSkills || [],
    },
  });

  const candidateViewFlag = useSelector(
    ({ candidate }: rootReducersState) => candidate.candidates.candidateViewFlag
  );

  // Watch skills inside form
  const formSkills = watch("skills") || [];

  // Add skill on comma or Enter
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      const trimmed = inputValue.trim().replace(",", "");
      if (
        trimmed &&
        !formSkills.find((s) => s.title.toLowerCase() === trimmed.toLowerCase())
      ) {
        setValue("skills", [...formSkills, { title: trimmed, desc: null }]);
      }
      setInputValue("");
    }
  };

  // Remove skill inside form
  const handleRemoveSkill = (index: number) => {
    const updated = [...formSkills];
    updated.splice(index, 1);
    setValue("skills", updated);
  };

  const openSkillsModal = () => {
    if (isPublicPage) return;
    reset({ skills: localSkills }); // sync form with latest local state
    setSkillsModal(true);
  };

  useEffect(() => {
    if (!openRequest || isPublicPage) return;
    openSkillsModal();
    onOpenHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest, isPublicPage]);

  const closeSkillsModal = () => {
    setSkillsModal(false);
  };

  const onSubmit = (data: { skills: Skill[] }) => {
    if (!data.skills || data.skills.length === 0) {
      FlashMessage("Please select at least one skill", "error");
      return;
    }
    dispatch(updateCandidateSkillsRequest({ skills: data.skills }));
  };

  // Keep local state in sync when prop changes
  useEffect(() => {
    setLocalSkills(initialSkills || []);
    reset({ skills: initialSkills || [] });
  }, [initialSkills, reset]);

  // Handle API response
  useEffect(() => {
    if (candidateViewFlag === "skills_success") {
      FlashMessage("Skills updated successfully");
      const updated = watch("skills");
      setLocalSkills(updated);
      setSkillsModal(false);
    } else if (candidateViewFlag === "skills_failed") {
      FlashMessage("Failed to update skills", "error");
    }
  }, [candidateViewFlag, watch]);

  return (
    <div className="accordion" id="profile-skills">
      <div className="accordion-head">
        <b>Skills</b>
        {!isPublicPage &&
          (!_.isEmpty(localSkills) ? (
            <div className="edit-icon" onClick={openSkillsModal}>
              <img src={EditIcon} alt="edit" />
            </div>
          ) : (
            <span onClick={openSkillsModal}>+Add</span>
          ))}
      </div>

      {!_.isEmpty(localSkills) && (
        <div className="accordion-body">
          <div className="skills-content">
            {localSkills.map((skill, index) => (
              <span key={index} className="skill-tag">
                {skill.title}
              </span>
            ))}
          </div>
        </div>
      )}

      {!isPublicPage && (
        <Modal
          visible={skillsModal}
          onClose={closeSkillsModal}
          className="candidate-profile-modal skills-modal"
          title={!_.isEmpty(localSkills) ? "Edit Skills" : "Add Skills"}
        >
          <div className="modal-body-wrapper">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-wrapper">
                <div className="form-group">
                  <Input
                    inputIcon={
                      <div className="input-icon">
                        <img src={SkillsIcon} alt="icon" />
                      </div>
                    }
                    className="input-with-icon"
                    externalLabel={{ label: "Skills" }}
                    placeholder="Type a skill and press comma"
                    value={inputValue}
                    onChange={(e: any) => setInputValue(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                  // inputRef={register("skill_name")} // optional if needed
                  />
                </div>

                {!_.isEmpty(formSkills) && (
                  <div className="selected-skills">
                    {formSkills.map((skill, index) => (
                      <div className="skill-tag" key={index}>
                        <span>{skill.title}</span>
                        <div
                          className="action"
                          onClick={() => handleRemoveSkill(index)}
                        >
                          <img src={CrossSmall} alt="cross" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="form-group suggested-row">
                  <label className="form-label">Suggested Skills</label>
                  <div className="suggested-skills">
                    {[
                      "JavaScript",
                      "React",
                      "Node.js",
                      "TypeScript",
                      "Python",
                      "Django",
                      "SQL",
                      "NoSQL",
                      "AWS",
                      "Docker",
                    ].map((skill, index) => (
                      <span
                        key={index}
                        className="suggested-skill"
                        onClick={() => {
                          if (!formSkills.find((s) => s.title === skill)) {
                            setValue("skills", [
                              ...formSkills,
                              { title: skill, desc: null },
                            ]);
                          }
                        }}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="btn-wrapper">
                  <Button
                    variant="outlined"
                    onClick={closeSkillsModal}
                    className="outlined"
                    color="none"
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button className="save-btn" type="submit">
                    Submit
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default SkillsSection;
